<template>
  <div class="app-container">
    <Header />
    <div class="blog-content-container">
      <div :class="mainContentClass">
        <div class="blog-header">
          <span class="blog-date">{{ date }}</span>
          <h1 class="blog-title">{{ title }}</h1>
          <div class="blog-meta">
            <div class="author-info">
              <img
                :src="authorAvatar || '/blog_assets/logo.png'"
                class="author-avatar"
              />
              <span class="author-name">{{ author || "Skyhost Team" }}</span>
            </div>
          </div>
        </div>

        <div class="school-differentiation-blog">
          <div class="blog-hero-image">
            <img :src="heroImage" alt="Blog hero image" class="hero-image" />
          </div>

          <section class="blog-section">
            <p>
              When every school claims "innovative" and "engaging," standing out
              requires more than buzzwords. Live drone tours give your
              institution a genuine edge—bringing dynamic, real-world
              experiences into the classroom and showing prospective families
              that you're committed to cutting-edge learning.
            </p>
            <p>
              Traditional field trips are expensive, logistically complex, and
              limited by geography. Pre-recorded videos can't answer students'
              live questions or respond to their curiosity in the moment. By
              contrast, Skyhost's live drone flights let your school stream
              aerial adventures from anywhere on Earth—while your teachers
              narrate in real time, pause for discussion, and guide students
              through on-the-spot investigations.
            </p>
          </section>

          <section class="blog-section">
            <h2>The Differentiator in Action</h2>
            <p>
              <strong>Immediate Engagement</strong><br />
              Nothing grabs attention like a drone swooping over a volcano's rim
              or skimming along a coral reef. Students don't just watch—they ask
              questions ("What causes that steam vent?"), make predictions
              ("Will the cliff undercut next?"), and sketch observations on
              their own. This level of active participation is rare in a typical
              lecture or video lesson.
            </p>
            <p>
              <strong>Brand-Building for Your School</strong><br />
              Offering live drone tours sends a clear message to parents and
              school boards: you invest in immersive, future-focused learning.
              Include highlights in your brochures, social media, and open-house
              presentations. Show families footage of your students leading a
              live exploration of Machu Picchu or the Great Barrier Reef—and
              watch inquiries rise.
            </p>
            <p>
              <strong>Measurable Outcomes</strong><br />
              Early adopters report:
            </p>
            <ul>
              <li>
                <strong>25% increase in open-house attendance</strong> when
                live-flight demos are included
              </li>
              <li>
                <strong>40% boost in geography assessment scores</strong>,
                thanks to improved spatial understanding
              </li>
              <li>
                <strong>30% higher conversion</strong> from campus visit to
                enrollment application
              </li>
            </ul>
          </section>

          <section class="blog-section">
            <h2>Benefits at a Glance</h2>
            <div class="table-container">
              <table class="benefits-table">
                <thead>
                  <tr>
                    <th>Benefit</th>
                    <th>Impact</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Real-Time Interaction</td>
                    <td>
                      Students drive the learning with live Q&A and requests
                    </td>
                  </tr>
                  <tr>
                    <td>Curriculum Alignment</td>
                    <td>
                      Flights map directly to state and national standards
                    </td>
                  </tr>
                  <tr>
                    <td>Marketing & Recruitment Tool</td>
                    <td>Unique experiences to showcase at events and online</td>
                  </tr>
                  <tr>
                    <td>Teacher Empowerment</td>
                    <td>
                      Pre-built lesson guides let educators focus on
                      facilitation, not setup
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="blog-section">
            <h2>Getting Started Is Simple</h2>
            <ol class="steps-list">
              <li>
                <strong>Pick Your Flight:</strong> Choose from dozens of
                ready-made tours—volcanoes, rainforests, historical sites—each
                with standards-aligned learning objectives.
              </li>
              <li>
                <strong>Schedule & Prep:</strong> Book a time slot via your
                Skyhost dashboard. Share the simple join-link with teachers and
                students.
              </li>
              <li>
                <strong>Launch & Learn:</strong> On the day of the tour, your
                teacher leads the session while our pilot flies. Pause the
                stream to discuss key concepts or extend an activity.
              </li>
              <li>
                <strong>Follow-Up:</strong> Use Skyhost's post-flight
                resources—high-res screenshots, quiz templates, and discussion
                prompts—to reinforce and assess learning.
              </li>
            </ol>
          </section>

          <section class="blog-section" style="border-bottom: none">
            <h2>Conclusion</h2>
            <p>
              In a crowded educational market, live drone tours aren't a
              gimmick—they're a transformative learning tool that boosts
              engagement, deepens understanding, and elevates your school's
              brand. If you're ready to differentiate your curriculum and give
              students an experience they'll never forget, explore Skyhost's
              School Packages at
              <a
                href="https://skyhost.ai/schools"
                target="_blank"
                class="blog-link"
                >skyhost.ai/schools</a
              >.
            </p>
            <p>
              Bring your geography lessons to life—and let your school be known
              for exploration without limits.
            </p>
          </section>
        </div>

        <div class="blog-footer">
          <div class="blog-tags" v-if="tags && tags.length">
            <span class="tag-label">Tags:</span>
            <span v-for="(tag, index) in tags" :key="index" class="blog-tag">
              {{ tag }}
            </span>
          </div>

          <div class="share-section">
            <span class="share-label">Share this article:</span>
            <div class="share-icons">
              <vue-feather
                type="twitter"
                size="18"
                class="share-icon"
                @click="shareOnTwitter"
              ></vue-feather>
              <vue-feather
                type="facebook"
                size="18"
                class="share-icon"
                @click="shareOnFacebook"
              ></vue-feather>
              <vue-feather
                type="linkedin"
                size="18"
                class="share-icon"
                @click="shareOnLinkedIn"
              ></vue-feather>
              <vue-feather
                type="mail"
                size="18"
                class="share-icon"
                @click="shareByEmail"
              ></vue-feather>
            </div>
          </div>

          <div class="related-posts" v-if="relatedBlogs.length">
            <h3 class="related-title">Related articles</h3>
            <div class="related-grid">
              <div
                v-for="relatedBlog in relatedBlogs"
                :key="relatedBlog.id"
                class="related-card"
                @click="navigateToBlog(relatedBlog.id)"
              >
                <img
                  :src="relatedBlog.image"
                  alt="Related blog image"
                  class="related-image"
                />
                <div class="related-content">
                  <h4 class="related-blog-title">{{ relatedBlog.title }}</h4>
                  <span class="related-date">{{ relatedBlog.date }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="back-to-blogs">
            <button class="blog-btn" @click="navigateToBlogs">
              <vue-feather
                type="arrow-left"
                size="16"
                class="back-icon"
              ></vue-feather>
              Back to all blogs
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "SchoolDifferentiationBlog",
  components: {
    Header,
    Footer,
  },
  data() {
    const blogId = "school-differentiation";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar || "/blog_assets/logo.png",
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    shareOnTwitter() {
      const text = `${this.title} - Check out this article by Skyhost`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(this.currentPageUrl)}`;
      window.open(url, "_blank");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareByEmail() {
      const subject = encodeURIComponent(this.title);
      const body = encodeURIComponent(
        `I thought you might find this article interesting: ${this.title}\n\n${this.currentPageUrl}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    this.relatedBlogs = Object.values(blogsRegistry)
      .filter((blog) => blog.id !== this.id)
      .slice(0, 2); // Limit to 2 related blogs
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });
    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.blog-header {
  margin-bottom: 2rem;
}

.blog-date {
  color: #666;
  font-size: 1rem;
}

.blog-title {
  font-size: 2.5rem;
  margin: 1rem 0;
  color: #000;
  line-height: 1.3;
}

.blog-meta {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  margin-right: 0.8rem;
  object-fit: contain;
}

.author-name {
  font-weight: 500;
  color: #333;
}

.school-differentiation-blog {
  font-family: "Inter", "Helvetica", sans-serif;
  line-height: 1.8;
  color: #333;
}

.blog-hero-image {
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  max-height: 500px;
  border-radius: 8px;
}

.blog-section {
  margin-bottom: 4rem;
  padding: 2rem 0;
  border-bottom: 1px solid #f0f0f0;
}

h2 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #000;
  font-weight: 600;
}

h3 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #000;
  font-weight: 500;
}

p {
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
}

ul,
ol {
  margin: 1.5rem 0 2rem 2rem;
}

li {
  margin-bottom: 1rem;
  font-size: 1.05rem;
}

.table-container {
  overflow-x: auto;
  margin: 2rem 0;
}

.benefits-table {
  width: 100%;
  border-collapse: collapse;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.benefits-table th {
  background-color: #f8f9fa;
  text-align: left;
  padding: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #eee;
}

.benefits-table td {
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
}

.benefits-table tr:hover {
  background-color: #f8f9fa;
}

.steps-list {
  counter-reset: steps-counter;
  list-style-type: none;
  margin-left: 0;
}

.steps-list li {
  position: relative;
  padding-left: 2.5rem;
  margin-bottom: 1.5rem;
}

.steps-list li::before {
  counter-increment: steps-counter;
  content: counter(steps-counter);
  position: absolute;
  left: 0;
  top: 0;
  width: 1.8rem;
  height: 1.8rem;
  background-color: #f0f0f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.blog-link {
  color: #1565c0;
  text-decoration: underline;
}

.blog-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.blog-tags {
  margin-bottom: 2rem;
}

.tag-label {
  font-weight: 500;
  margin-right: 0.5rem;
}

.blog-tag {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  margin: 0.3rem;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #666;
}

.share-section {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.share-label {
  font-weight: 500;
  margin-right: 1rem;
}

.share-icons {
  display: flex;
  gap: 1rem;
}

.share-icon {
  cursor: pointer;
  color: #555;
}

.share-icon:hover {
  color: #1565c0;
}

.related-posts {
  margin-bottom: 3rem;
}

.related-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #000;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.related-card:hover {
  transform: translateY(-3px);
}

.related-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.related-content {
  padding: 1rem;
}

.related-blog-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #000;
}

.related-date {
  font-size: 0.8rem;
  color: #666;
}

.back-to-blogs {
  margin-top: 3rem;
  text-align: center;
}

.blog-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: #f0f0f0;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.blog-btn:hover {
  background-color: #e0e0e0;
}

.back-icon {
  margin-right: 0.5rem;
}

@media (max-width: 768px) {
  .blog-title {
    font-size: 2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
